import { useEffect, useState } from 'react';
import './NewVersionBanner.css';

/**
 * 새 버전이 배포됐는데 이 탭은 아직 예전 화면인지 알린다.
 *
 * 이 앱은 SPA 라, 배포(web/dist 교체) 전에 열어 둔 탭은 F5 를 누르기 전까지 예전
 * JS 로 계속 돈다. 2026-11-04 에 실제로 그랬다 - 고친 경품 수정 모달이 배포된
 * 뒤에도 열려 있던 탭에서는 예전 모달이 떠서 "여전히 깨진다", "소수점 확률을
 * 넣으면 유효값을 넣으라고 한다"는 보고가 두 번 왔다. 예전 모달로 행운상을 저장하면
 * 시각이 00:00 으로 바뀌는 문제도 그대로 남는다.
 *
 * 그래서 index.html 을 캐시 없이 다시 받아, 거기 적힌 메인 번들 이름이 지금 탭이
 * 실행 중인 번들과 다르면 띠를 띄운다. 자동으로 새로고침하지는 않는다 - 입력하던
 * 폼이 날아간다.
 *
 * 개발 서버(vite dev)와 테스트(jsdom)에서는 /assets/index-*.js 스크립트가 없어서
 * 아무 것도 하지 않는다.
 */

/** 같은 탭에서 너무 자주 받지 않게 - 포커스가 오갈 때마다 부르므로. */
const MIN_INTERVAL_MS = 30_000;
const POLL_INTERVAL_MS = 5 * 60_000;

const BUNDLE_RE = /\/assets\/index-[\w-]+\.js/;

/** 지금 탭이 실행 중인 메인 번들 경로 (예: /assets/index-BqIaInLQ.js). 빌드된 화면이 아니면 null. */
export function currentBundle(doc: Document = document): string | null {
  const script = doc.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/index-"]');
  const match = script ? BUNDLE_RE.exec(script.getAttribute('src') ?? '') : null;
  return match ? match[0] : null;
}

/** 서버의 index.html 이 가리키는 메인 번들 경로. 못 읽으면 null (네트워크 오류 등은 조용히 넘긴다). */
export async function latestBundle(): Promise<string | null> {
  try {
    const res = await fetch('/', { cache: 'no-store', credentials: 'same-origin' });
    if (!res.ok) return null;
    const match = BUNDLE_RE.exec(await res.text());
    return match ? match[0] : null;
  } catch {
    return null;
  }
}

export function useNewVersionAvailable(): boolean {
  const [stale, setStale] = useState(false);

  useEffect(() => {
    const running = currentBundle();
    if (!running) return;

    let lastCheck = 0;
    let cancelled = false;

    async function check() {
      const now = Date.now();
      if (now - lastCheck < MIN_INTERVAL_MS) return;
      lastCheck = now;
      const latest = await latestBundle();
      if (!cancelled && latest && latest !== running) setStale(true);
    }

    function onVisible() {
      if (document.visibilityState === 'visible') void check();
    }

    void check();
    const timer = window.setInterval(() => void check(), POLL_INTERVAL_MS);
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  return stale;
}

export function NewVersionBanner() {
  const stale = useNewVersionAvailable();
  if (!stale) return null;
  return (
    <div className="new-version-banner" role="alert">
      <span>
        <b>새 버전이 배포되었습니다.</b> 지금 화면은 예전 버전이라 고친 기능이 적용되지 않습니다 - 새로고침해주세요.
      </span>
      <button type="button" className="btn btn--sm new-version-banner__btn" onClick={() => window.location.reload()}>
        새로고침
      </button>
    </div>
  );
}
